import React, { useEffect, useState } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { Sun, Moon, ChevronRight, Home, Building2, LogOut, HelpCircle, Users as UsersIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useOnboarding } from "@/context/OnboardingContext";
import api from "@/lib/api";
import CommandPalette, { CommandPaletteTrigger } from "@/components/CommandPalette";
import NotificationBell from "@/components/NotificationBell";

const ROUTE_LABELS = {
  "": "Dashboard",
  "rota": "Rota & Visits",
  "staff-schedule": "Staff Schedule",
  "client-care-cost": "Client Care Cost",
  "payroll": "Payroll & Staff Hours",
  "mar-chart": "MAR Chart",
  "cd-ledger": "CD Ledger",
  "delivery-notes": "Delivery Notes",
  "my-shifts": "My Shifts",
  "calendar": "Appointments",
  "audit": "Audit Centre",
  "care-plans": "Care Plans",
  "risk-register": "Risk Register",
  "time-off-requests": "Leave & Holidays",
  "family-portal": "Family Portal",
  "family-inbox": "Family Inbox",
  "sites": "Sites",
  "users": "Users",
  "clients": "Service Users",
  "mobile": "Mobile View",
  "training": "Training",
  "supervisions": "Supervisions",
  "audits": "Audits",
  "medication_errors": "Medication Errors",
  "incidents": "Incidents",
  "complaints": "Complaints",
  "outcomes": "Outcomes",
  "maintenance": "Maintenance",
  "staff": "Staff",
};

function humanize(segment) {
  return ROUTE_LABELS[segment] || segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function Breadcrumbs() {
  const location = useLocation();
  const segments = location.pathname.split("/").filter(Boolean);
  return (
    <nav aria-label="Breadcrumb" data-testid="topbar-breadcrumbs" className="flex items-center text-xs text-slate-500 dark:text-slate-400 min-w-0">
      <Link to="/" className="inline-flex items-center gap-1 hover:text-slate-900 dark:hover:text-slate-100 transition-colors shrink-0">
        <Home className="w-3.5 h-3.5" />
      </Link>
      {segments.map((seg, i) => {
        const to = "/" + segments.slice(0, i + 1).join("/");
        const isLast = i === segments.length - 1;
        // Skip UUID-looking segments in the label but still render as clickable
        const label = /^[0-9a-f]{8}-/i.test(seg) ? "…" : humanize(seg);
        return (
          <span key={to} className="inline-flex items-center min-w-0">
            <ChevronRight className="w-3 h-3 mx-1 text-slate-300 dark:text-slate-600 shrink-0" />
            {isLast ? (
              <span className="font-medium text-slate-900 dark:text-slate-100 truncate" data-testid={`crumb-${seg}`}>{label}</span>
            ) : (
              <Link to={to} className="hover:text-slate-900 dark:hover:text-slate-100 transition-colors truncate">{label}</Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

// Deterministic per-organisation accent colour. Given the same `org_id`
// we always produce the same swatch, so operators can glance-check "am I
// inside the right tenant?" without reading text. The palette is 10
// hand-picked hues that read cleanly in both light and dark modes.
const ORG_PALETTE = [
  { dot: "#10b981", text: "#047857", darkText: "#6ee7b7", bg: "#ecfdf5", darkBg: "#022c22" }, // emerald
  { dot: "#0ea5e9", text: "#0369a1", darkText: "#7dd3fc", bg: "#f0f9ff", darkBg: "#082f49" }, // sky
  { dot: "#8b5cf6", text: "#6d28d9", darkText: "#c4b5fd", bg: "#f5f3ff", darkBg: "#2e1065" }, // violet
  { dot: "#f59e0b", text: "#b45309", darkText: "#fcd34d", bg: "#fffbeb", darkBg: "#451a03" }, // amber
  { dot: "#ef4444", text: "#b91c1c", darkText: "#fca5a5", bg: "#fef2f2", darkBg: "#450a0a" }, // rose
  { dot: "#14b8a6", text: "#0f766e", darkText: "#5eead4", bg: "#f0fdfa", darkBg: "#042f2e" }, // teal
  { dot: "#ec4899", text: "#be185d", darkText: "#f9a8d4", bg: "#fdf2f8", darkBg: "#500724" }, // pink
  { dot: "#6366f1", text: "#4338ca", darkText: "#a5b4fc", bg: "#eef2ff", darkBg: "#1e1b4b" }, // indigo
  { dot: "#84cc16", text: "#4d7c0f", darkText: "#bef264", bg: "#f7fee7", darkBg: "#1a2e05" }, // lime
  { dot: "#f97316", text: "#c2410c", darkText: "#fdba74", bg: "#fff7ed", darkBg: "#431407" }, // orange
];

function _hashStringToInt(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function orgAccent(org) {
  // Platform-Admin override wins if the org doc carries a `brand_color`
  // (7-char hex). Otherwise derive deterministically from the org id.
  if (org?.brand_color && /^#[0-9a-fA-F]{6}$/.test(org.brand_color)) {
    return {
      dot: org.brand_color, text: org.brand_color, darkText: org.brand_color,
      bg: org.brand_color + "1a", darkBg: org.brand_color + "26",
    };
  }
  const key = org?.id || org?.slug || org?.name || "org";
  return ORG_PALETTE[_hashStringToInt(String(key)) % ORG_PALETTE.length];
}

function DemoEnvBadge() {
  // Unauthenticated read of /api/env — the endpoint never returns any
  // secret, Mongo host or DB name. We only render the pill when the
  // server declares itself a Demo deployment (APP_ENV=demo).
  const [env, setEnv] = React.useState(null);
  React.useEffect(() => {
    api.get("/env")
      .then((r) => setEnv(r.data))
      .catch(() => setEnv(null));
  }, []);
  if (!env?.is_demo) return null;
  return (
    <span
      className="hidden md:inline-flex items-center h-7 px-2 rounded-md border text-[10px] font-bold uppercase tracking-widest mr-1
                 border-amber-400 text-amber-800 bg-amber-100
                 dark:border-amber-500 dark:text-amber-200 dark:bg-amber-900/40"
      title="This is a Demo environment. Data shown here is not real customer data."
      data-testid="topbar-demo-env-badge"
    >
      {env.label || "DEMO"}
    </span>
  );
}

function OrgBadge() {
  const { user } = useAuth();
  const [org, setOrg] = React.useState(null);
  React.useEffect(() => {
    if (!user) return;
    api.get("/organisations/mine").then((r) => setOrg(r.data)).catch(() => {});
  }, [user?.id]);
  if (!org?.name) return null;
  const accent = orgAccent(org);
  return (
    <span
      className="hidden md:inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-md border text-xs font-medium mr-1"
      style={{
        color: `var(--org-fg, ${accent.text})`,
        borderColor: `${accent.dot}55`,
        background: `${accent.dot}10`,
      }}
      title={`Signed in to ${org.name}. Data is scoped to this organisation.`}
      data-testid="topbar-org-badge"
    >
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ background: accent.dot, boxShadow: `0 0 0 2px ${accent.dot}22` }}
        data-testid="topbar-org-badge-dot"
      />
      <span className="max-w-[180px] truncate">
        {org.name}
      </span>
    </span>
  );
}

function SiteSwitcher() {
  const { user, setUser } = useAuth();
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "manager") return;
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
  }, [user?.role]);

  if (user?.role !== "admin" && user?.role !== "manager") return null;

  const currentSite = sites.find((s) => s.id === user?.site_id);
  const label = currentSite?.name || "All sites";

  const pick = async (siteId) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", { site_id: siteId });
      setUser(data);
      window.location.reload(); // refresh scoped data
    } catch { /* toast handled by api */ }
    finally { setSaving(false); setOpen(false); }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={saving}
        data-testid="topbar-site-switcher"
        className="inline-flex items-center gap-2 h-8 px-3 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-400 dark:hover:border-slate-500 transition-colors text-xs"
      >
        <Building2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
        <span className="font-medium text-slate-800 dark:text-slate-100 max-w-[140px] truncate">{label}</span>
        <span className="text-slate-400">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-1 z-50 min-w-[200px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg rounded-md py-1"
            data-testid="topbar-site-menu"
          >
            <button
              onClick={() => pick(null)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 dark:hover:bg-slate-800 ${!user?.site_id ? "text-emerald-700 dark:text-emerald-400 font-semibold" : "text-slate-700 dark:text-slate-200"}`}
              data-testid="topbar-site-all"
            >
              All sites
            </button>
            {sites.map((s) => (
              <button
                key={s.id}
                onClick={() => pick(s.id)}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 dark:hover:bg-slate-800 ${s.id === user?.site_id ? "text-emerald-700 dark:text-emerald-400 font-semibold" : "text-slate-700 dark:text-slate-200"}`}
                data-testid={`topbar-site-${s.id}`}
              >
                {s.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      onClick={toggle}
      data-testid="topbar-theme-toggle"
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-400 dark:hover:border-slate-500 transition-colors"
    >
      {isDark
        ? <Sun className="w-3.5 h-3.5 text-amber-400" />
        : <Moon className="w-3.5 h-3.5 text-slate-700" />}
    </button>
  );
}

function UserChip() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const initials = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();
  const roleLabel = (user?.role || "").replace(/_/g, " ");
  const roleTone =
    user?.role === "admin" ? "bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300 border-amber-300"
      : user?.role === "manager" ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/20 dark:text-emerald-300 border-emerald-300"
      : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200 border-slate-300";

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid="topbar-user-chip"
        className="inline-flex items-center gap-2 h-8 pl-1 pr-3 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-400 dark:hover:border-slate-500 transition-colors"
      >
        <span className="w-6 h-6 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 dark:from-slate-700 dark:to-slate-950 flex items-center justify-center text-[10px] font-semibold text-white shrink-0">
          {initials}
        </span>
        <span className="text-xs font-medium text-slate-800 dark:text-slate-100 max-w-[110px] truncate hidden sm:inline">
          {user?.name || user?.email}
        </span>
        {roleLabel && (
          <span className={`text-[9px] uppercase tracking-widest font-semibold border px-1.5 py-0.5 rounded-sm ${roleTone}`} data-testid="topbar-role-chip">
            {roleLabel}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-1 z-50 min-w-[220px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg rounded-md overflow-hidden"
            data-testid="topbar-user-menu"
          >
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700">
              <div className="text-xs font-medium text-slate-900 dark:text-slate-100 truncate">{user?.name || user?.email}</div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{user?.email}</div>
            </div>
            <NavLink
              to="/mobile"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Open Mobile view
            </NavLink>
            <button
              onClick={() => { setOpen(false); logout(); }}
              data-testid="topbar-signout"
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border-t border-slate-100 dark:border-slate-800"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function OnlineChip() {
  const { user: me } = useAuth();
  const [data, setData] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [busyId, setBusyId] = React.useState(null);
  const load = React.useCallback(() => api.get("/presence/online").then((r) => setData(r.data)).catch(() => {}), []);
  React.useEffect(() => {
    let alive = true;
    const run = () => { if (alive) load(); };
    run();
    const t = setInterval(run, 30_000);
    return () => { alive = false; clearInterval(t); };
  }, [load]);
  const isAdmin = me?.role === "admin";
  const forceSignOut = async (u) => {
    if (busyId) return;
    if (!window.confirm(`Force sign out ${u.name || u.email}?\nAny active session they have will be immediately invalidated.`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/presence/users/${u.id}/force-signout`);
      await load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not sign out this user");
    } finally { setBusyId(null); }
  };
  const count = data?.count ?? 0;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        data-testid="topbar-online"
        title={`${count} user${count === 1 ? "" : "s"} online in the last ${data?.window_minutes || 5} min`}
        className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
      >
        <span className="relative flex w-2 h-2">
          <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
          <span className="relative inline-flex rounded-full w-2 h-2 bg-emerald-500" />
        </span>
        <UsersIcon className="w-3.5 h-3.5" />
        <span className="text-[11px] font-semibold tabular-nums" data-testid="topbar-online-count">{count}</span>
        <span className="text-[10px] uppercase tracking-widest text-slate-500 hidden md:inline">online</span>
      </button>
      {open && data && (
        <div className="absolute right-0 mt-1 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg z-50 p-2" data-testid="topbar-online-panel">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-1 pb-1">
            {count} online · last {data.window_minutes} min
          </div>
          {Object.keys(data.by_role || {}).length > 0 && (
            <div className="flex flex-wrap gap-1 px-1 pb-2">
              {Object.entries(data.by_role).map(([role, n]) => (
                <span key={role} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 capitalize">
                  {role}: <b className="tabular-nums">{n}</b>
                </span>
              ))}
            </div>
          )}
          <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
            {(data.users || []).slice(0, 20).map((u) => {
              const isMe = me?.id === u.id;
              return (
                <li key={u.id} className="flex items-center gap-2 px-1 py-1.5 text-xs" data-testid={`online-user-${u.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-900 dark:text-slate-100 truncate">{u.name || u.email}{isMe && <span className="ml-1 text-[9px] text-emerald-700">(you)</span>}</div>
                    <div className="text-[10px] text-slate-500 capitalize">{u.role}</div>
                  </div>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                  {isAdmin && !isMe && (
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); forceSignOut(u); }}
                      disabled={busyId === u.id}
                      title="Force sign out"
                      data-testid={`force-signout-${u.id}`}
                      className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      {busyId === u.id ? "…" : "Sign out"}
                    </button>
                  )}
                </li>
              );
            })}
            {(data.users || []).length === 0 && (
              <li className="text-[11px] text-slate-500 px-1 py-2">Just you right now.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function HelpButton() {
  const { setOpen, status } = useOnboarding();
  const pending = status ? (status.total - status.completed_count) : 0;
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      data-testid="topbar-help"
      title="Help Centre · guided tour · onboarding"
      className="relative h-7 w-7 flex items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:text-sky-700 hover:border-sky-300 transition-colors"
      aria-label="Help Centre"
    >
      <HelpCircle className="w-4 h-4" />
      {pending > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-sky-600 text-white text-[9px] font-bold flex items-center justify-center tabular-nums"
          data-testid="topbar-help-badge"
        >
          {pending}
        </span>
      )}
    </button>
  );
}

export default function TopBar() {
  return (
    <div
      className="h-11 shrink-0 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 flex items-center justify-between gap-3"
      data-testid="topbar"
    >
      <Breadcrumbs />
      <div className="flex items-center gap-2">
        <CommandPaletteTrigger />
        <DemoEnvBadge />
        <OrgBadge />
        <SiteSwitcher />
        <OnlineChip />
        <NotificationBell />
        <HelpButton />
        <ThemeToggle />
        <UserChip />
      </div>
      <CommandPalette />
    </div>
  );
}
